import type { ReactNode } from 'react'
import type { QuickSearchProductResult } from '@/types/searchTypes'
import clsx from 'clsx'
import { useEffect } from 'react'
import { useOverflowList } from 'react-responsive-overflow-list'
import { EmptyState } from './EmptyState'
import { SearchCard } from './SearchCard'

interface CardSectionProps {
  query: string
  results: QuickSearchProductResult[]
  onResultSelect: (result: QuickSearchProductResult) => void
  action?: ReactNode
  gridColumns?: number
  enableOverflow?: boolean
  responsiveGrid?: boolean
  onOverflowCountChange?: (hiddenCount: number | null) => void
}

export function CardSection({
  query,
  results,
  onResultSelect,
  action,
  gridColumns = 7,
  enableOverflow = false,
  responsiveGrid = false,
  onOverflowCountChange,
}: Readonly<CardSectionProps>) {
  const { containerRef, visibleCount, hiddenCount, phase } = useOverflowList<HTMLDivElement>({
    itemCount: results.length,
    maxRows: 1,
  })

  useEffect(() => {
    onOverflowCountChange?.(enableOverflow && phase !== 'measuring' ? hiddenCount : null)
  }, [enableOverflow, hiddenCount, onOverflowCountChange, phase])

  return (
    <section className="quick-search__section">
      {action && <div className="quick-search__section-action">{action}</div>}
      {results.length === 0
        ? <EmptyState />
        : (
            <div
              ref={enableOverflow ? containerRef : undefined}
              className={clsx('quick-search__cards', {
                'quick-search__cards--overflow-enabled': enableOverflow,
                'quick-search__cards--responsive-grid': responsiveGrid,
              })}
              style={{ '--card-grid-columns': gridColumns } as React.CSSProperties}
            >
              {results.map((result, index) => {
                const isVisible = !enableOverflow || phase === 'measuring' || index < visibleCount

                if (!isVisible)
                  return null

                return (
                  <SearchCard
                    key={result.id}
                    query={query}
                    result={result}
                    onClick={() => onResultSelect(result)}
                  />
                )
              })}
            </div>
          )}
    </section>
  )
}
